import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getRivhitItemPricing } from '../api/client';
import { useInfiniteScroll } from '../hooks/useInfiniteScroll';
import { useRivhitData, rivhitDisplayName } from '../context/RivhitDataContext';
import type { RivhitCustomer, RivhitItem } from '../context/RivhitDataContext';

const BATCH_SIZE = 40;

interface RivhitPriceListItem {
  price_list_id?: number;
  item_id?: number;
  discount_percent?: number;
  price_nis?: number;
  quantity?: number;
  item_name?: string;
  item_part_num?: string;
  original_price_nis?: number;
}

function fuzzyMatch(query: string, target: string): number {
  const q = query.toLowerCase();
  const t = target.toLowerCase();

  if (t.includes(q)) return 0;

  let qi = 0;
  let score = 0;
  let lastMatchIdx = -1;

  for (let ti = 0; ti < t.length && qi < q.length; ti++) {
    if (t[ti] === q[qi]) {
      if (lastMatchIdx >= 0) {
        score += ti - lastMatchIdx - 1;
      }
      lastMatchIdx = ti;
      qi++;
    }
  }

  return qi === q.length ? score + 1 : -1;
}

function fuzzySearch(items: RivhitItem[], query: string): RivhitItem[] {
  if (!query.trim()) return items;

  const q = query.trim();

  return items
    .map((item) => {
      const fields = [
        item.item_name,
        item.catalog_number,
        item.item_group_name,
        item.barcode,
      ].filter(Boolean) as string[];

      let bestScore = -1;
      for (const field of fields) {
        const score = fuzzyMatch(q, field);
        if (score >= 0 && (bestScore < 0 || score < bestScore)) {
          bestScore = score;
        }
      }

      return { item, score: bestScore };
    })
    .filter((entry) => entry.score >= 0)
    .sort((a, b) => a.score - b.score)
    .map((entry) => entry.item);
}

function formatCurrency(value?: number | null): string {
  if (value == null) return '—';
  return new Intl.NumberFormat('he-IL', {
    style: 'currency',
    currency: 'ILS',
  }).format(value);
}

export default function Items() {
  const { t } = useTranslation();
  const {
    configured,
    items: allItems,
    itemsLoading,
    ensureItemsLoaded,
    refreshItems,
    customers,
    customersLoading,
    ensureCustomersLoaded,
  } = useRivhitData();

  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [selectedItem, setSelectedItem] = useState<RivhitItem | null>(null);
  const [pricingItem, setPricingItem] = useState<RivhitItem | null>(null);
  const [customerQuery, setCustomerQuery] = useState('');
  const [selectedCustomerId, setSelectedCustomerId] = useState<number | null>(null);
  const [pricingRows, setPricingRows] = useState<RivhitPriceListItem[]>([]);
  const [pricingLoading, setPricingLoading] = useState(false);
  const [pricingError, setPricingError] = useState<string | null>(null);
  const [hasPricingRequest, setHasPricingRequest] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(-1);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    if (!configured) return;
    ensureItemsLoaded();
  }, [configured, ensureItemsLoaded]);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setDebouncedQuery(searchQuery);
    }, 200);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [searchQuery]);

  const filtered = useMemo(() => fuzzySearch(allItems, debouncedQuery), [allItems, debouncedQuery]);
  const { visible: paged, hasMore, sentinelRef } = useInfiniteScroll(filtered, BATCH_SIZE);

  const customerOptions = useMemo(() => {
    const q = customerQuery.trim().toLowerCase();
    const base = q
      ? customers.filter((customer) => {
          const fields = [
            rivhitDisplayName(customer),
            customer.phone,
            customer.email,
            customer.city,
          ].filter(Boolean) as string[];
          return fields.some((field) => field.toLowerCase().includes(q));
        })
      : customers;
    return base.slice(0, 50);
  }, [customerQuery, customers]);

  const selectedCustomer = useMemo(
    () => customers.find((customer) => customer.customer_id === selectedCustomerId) ?? null,
    [customers, selectedCustomerId],
  );

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Auto-fetch pricing when customer is selected
  const loadPricingFor = useCallback(async (itemId: number, custId: number) => {
    setPricingLoading(true);
    setPricingError(null);
    setHasPricingRequest(true);
    try {
      const response = await getRivhitItemPricing(itemId, custId);
      setPricingRows(response.data ?? []);
    } catch (err: any) {
      setPricingRows([]);
      setPricingError(err?.response?.data?.detail || err?.message || 'Failed to load pricing');
    } finally {
      setPricingLoading(false);
    }
  }, []);

  const selectCustomer = useCallback((customer: RivhitCustomer) => {
    setSelectedCustomerId(customer.customer_id);
    setCustomerQuery(rivhitDisplayName(customer));
    setDropdownOpen(false);
    setHighlightIndex(-1);
    if (pricingItem?.item_id && customer.customer_id) {
      loadPricingFor(pricingItem.item_id, customer.customer_id);
    }
  }, [pricingItem, loadPricingFor]);

  const clearCustomer = useCallback(() => {
    setSelectedCustomerId(null);
    setCustomerQuery('');
    setPricingRows([]);
    setPricingError(null);
    setHasPricingRequest(false);
    setDropdownOpen(false);
    inputRef.current?.focus();
  }, []);

  const handleCustomerInputChange = useCallback((value: string) => {
    setCustomerQuery(value);
    setSelectedCustomerId(null);
    setPricingRows([]);
    setPricingError(null);
    setHasPricingRequest(false);
    setDropdownOpen(true);
    setHighlightIndex(-1);
  }, []);

  const handleCustomerKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (!dropdownOpen) {
      if (e.key === 'ArrowDown' || e.key === 'Enter') {
        setDropdownOpen(true);
        e.preventDefault();
      }
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightIndex((prev) => Math.min(prev + 1, customerOptions.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightIndex((prev) => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter' && highlightIndex >= 0 && highlightIndex < customerOptions.length) {
      e.preventDefault();
      selectCustomer(customerOptions[highlightIndex]);
    } else if (e.key === 'Escape') {
      setDropdownOpen(false);
      setHighlightIndex(-1);
    }
  }, [dropdownOpen, highlightIndex, customerOptions, selectCustomer]);

  const openPricing = async (item: RivhitItem) => {
    setPricingItem(item);
    setCustomerQuery('');
    setSelectedCustomerId(null);
    setPricingRows([]);
    setPricingError(null);
    setHasPricingRequest(false);
    setDropdownOpen(false);
    setHighlightIndex(-1);
    await ensureCustomersLoaded();
  };

  const closePricing = () => {
    setPricingItem(null);
    setCustomerQuery('');
    setSelectedCustomerId(null);
    setPricingRows([]);
    setPricingError(null);
    setHasPricingRequest(false);
    setDropdownOpen(false);
    setHighlightIndex(-1);
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('items.title')}</h1>
          <p className="mt-0.5 text-sm text-gray-500">{t('items.subtitle')}</p>
        </div>
        <button
          onClick={refreshItems}
          disabled={itemsLoading}
          className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <span className={itemsLoading ? 'animate-spin' : ''}>🔄</span>
          {t('items.refresh')}
        </button>
      </div>

      <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
        <div className="relative">
          <span className="pointer-events-none absolute inset-y-0 ltr:left-3 rtl:right-3 flex items-center text-gray-400">
            🔍
          </span>
          <input
            type="text"
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
            placeholder={t('items.searchPlaceholder')}
            className="w-full rounded-md border border-gray-300 bg-gray-50 py-2 text-sm outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500 ltr:pl-10 ltr:pr-3 rtl:pl-3 rtl:pr-10"
          />
        </div>
        {debouncedQuery && !itemsLoading && (
          <p className="mt-2 text-xs text-gray-500">
            {filtered.length} {t('items.resultsFound')}
          </p>
        )}
      </div>

      {itemsLoading ? (
        <div className="p-8 text-center text-sm text-gray-400">{t('common.loading')}</div>
      ) : paged.length === 0 ? (
        <div className="p-8 text-center text-sm text-gray-400">{t('items.noResults')}</div>
      ) : (
        <>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
            {paged.map((item) => {
              const isSelected = selectedItem?.item_id === item.item_id;
              return (
                <div
                  key={`${item.item_id ?? item.catalog_number ?? item.item_name}`}
                  className={`rounded-lg border p-4 transition-all ${
                    isSelected
                      ? 'border-primary-500 bg-primary-50 ring-1 ring-primary-500'
                      : 'border-gray-200 bg-white hover:border-gray-300 hover:shadow-md'
                  }`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <button
                      onClick={() => setSelectedItem(item)}
                      className="min-w-0 flex-1 text-start"
                    >
                      <h3 className="truncate font-semibold text-gray-900">
                        {item.item_name || t('items.unnamedItem')}
                      </h3>
                      <div className="mt-2 space-y-1 text-xs text-gray-500">
                        <p>{t('items.catalogPrice')}: {formatCurrency(item.price)}</p>
                        <p>{t('items.group')}: {item.item_group_name || '—'}</p>
                        <p>{t('items.catalogNumber')}: {item.catalog_number || '—'}</p>
                        <p>{t('items.barcode')}: {item.barcode || '—'}</p>
                      </div>
                    </button>
                    <button
                      onClick={() => openPricing(item)}
                      disabled={!item.item_id}
                      className="shrink-0 rounded-md bg-primary-600 px-3 py-1.5 text-xs font-medium text-white transition-colors hover:bg-primary-700 disabled:cursor-not-allowed disabled:bg-gray-300"
                    >
                      {t('items.viewPricing')}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          {hasMore && (
            <div ref={sentinelRef} className="flex justify-center py-4">
              <span className="text-sm text-gray-400">
                {paged.length} / {filtered.length}
              </span>
            </div>
          )}
        </>
      )}

      {pricingItem && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          onClick={closePricing}
        >
          <div
            className="flex max-h-[85vh] w-full max-w-2xl flex-col overflow-hidden rounded-xl bg-white shadow-2xl"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="border-b border-gray-100 px-6 py-4">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <h2 className="text-lg font-semibold text-gray-900">{pricingItem.item_name || t('items.unnamedItem')}</h2>
                </div>
                <button
                  onClick={closePricing}
                  className="text-sm text-gray-400 transition-colors hover:text-gray-700"
                >
                  ✕
                </button>
              </div>
            </div>

            <div className="space-y-4 overflow-y-auto px-6 py-4">
              {/* Catalog info section */}
              <div className="grid grid-cols-2 gap-3 rounded-lg border border-gray-200 bg-gray-50 p-4 text-sm sm:grid-cols-4">
                <div>
                  <p className="text-xs font-medium uppercase tracking-wider text-gray-500">{t('items.catalogPrice')}</p>
                  <p className="mt-1 text-base font-semibold text-gray-900">{formatCurrency(pricingItem.price)}</p>
                </div>
                <div>
                  <p className="text-xs font-medium uppercase tracking-wider text-gray-500">{t('items.catalogNumber')}</p>
                  <p className="mt-1 font-medium text-gray-700">{pricingItem.catalog_number || '—'}</p>
                </div>
                <div>
                  <p className="text-xs font-medium uppercase tracking-wider text-gray-500">{t('items.group')}</p>
                  <p className="mt-1 font-medium text-gray-700">{pricingItem.item_group_name || '—'}</p>
                </div>
                <div>
                  <p className="text-xs font-medium uppercase tracking-wider text-gray-500">{t('items.barcode')}</p>
                  <p className="mt-1 font-medium text-gray-700">{pricingItem.barcode || '—'}</p>
                </div>
              </div>

              {/* Customer autocomplete search */}
              <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                <p className="text-sm font-medium text-gray-700">{t('items.pricingForCustomer')}</p>
                <div className="relative mt-3" ref={dropdownRef}>
                  <div className="relative">
                    <span className="pointer-events-none absolute inset-y-0 ltr:left-3 rtl:right-3 flex items-center text-gray-400">
                      🔍
                    </span>
                    <input
                      ref={inputRef}
                      type="text"
                      value={customerQuery}
                      onChange={(e) => handleCustomerInputChange(e.target.value)}
                      onFocus={() => { if (!selectedCustomerId) setDropdownOpen(true); }}
                      onKeyDown={handleCustomerKeyDown}
                      placeholder={t('items.customerSearchPlaceholder')}
                      className="w-full rounded-md border border-gray-300 bg-white py-2 text-sm outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500 ltr:pl-10 ltr:pr-8 rtl:pl-8 rtl:pr-10"
                    />
                    {selectedCustomerId && (
                      <button
                        onClick={clearCustomer}
                        className="absolute inset-y-0 ltr:right-2 rtl:left-2 flex items-center text-gray-400 hover:text-gray-600"
                        aria-label="Clear"
                      >
                        ✕
                      </button>
                    )}
                  </div>

                  {dropdownOpen && !selectedCustomerId && (
                    <div className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md border border-gray-200 bg-white shadow-lg">
                      {customersLoading ? (
                        <div className="px-4 py-3 text-sm text-gray-500">{t('items.loadingCustomers')}</div>
                      ) : customerOptions.length === 0 ? (
                        <div className="px-4 py-3 text-sm text-gray-400">
                          {customerQuery.trim() ? t('items.noCustomersFound') : t('items.typeToSearch')}
                        </div>
                      ) : (
                        customerOptions.map((customer, idx) => (
                          <button
                            key={customer.customer_id}
                            onClick={() => selectCustomer(customer)}
                            onMouseEnter={() => setHighlightIndex(idx)}
                            className={`flex w-full items-center justify-between px-4 py-2.5 text-start text-sm transition-colors ${
                              idx === highlightIndex
                                ? 'bg-primary-50 text-primary-700'
                                : 'text-gray-700 hover:bg-gray-50'
                            }`}
                          >
                            <span className="truncate font-medium">{rivhitDisplayName(customer)}</span>
                            {customer.price_list_id && (
                              <span className="ml-2 shrink-0 rounded bg-gray-100 px-1.5 py-0.5 text-xs text-gray-500">
                                {t('items.priceListShort')} #{customer.price_list_id}
                              </span>
                            )}
                          </button>
                        ))
                      )}
                    </div>
                  )}
                </div>
                {selectedCustomer && (
                  <p className="mt-2 text-xs text-gray-500">
                    {t('items.selectedCustomer')}: {rivhitDisplayName(selectedCustomer)}
                    {selectedCustomer.price_list_id ? ` · ${t('items.priceListShort')} #${selectedCustomer.price_list_id}` : ''}
                  </p>
                )}
              </div>

              {pricingError && (
                <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                  {pricingError}
                </div>
              )}

              {!pricingError && !pricingLoading && hasPricingRequest && selectedCustomerId && pricingRows.length === 0 && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                  {t('items.noCustomerPricing')}
                </div>
              )}

              {pricingRows.length > 0 && (
                <div className="overflow-hidden rounded-lg border border-gray-200">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-gray-50 text-xs uppercase tracking-wider text-gray-600">
                        <th className="px-4 py-3 text-start font-medium">{t('items.quantityBreak')}</th>
                        <th className="px-4 py-3 text-end font-medium">{t('items.originalPrice')}</th>
                        <th className="px-4 py-3 text-end font-medium">{t('items.discount')}</th>
                        <th className="px-4 py-3 text-end font-medium">{t('items.customerPrice')}</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 bg-white">
                      {pricingRows.map((row, index) => (
                        <tr key={`${row.price_list_id}-${row.item_id}-${index}`}>
                          <td className="px-4 py-3 text-gray-700">{row.quantity ?? 1}</td>
                          <td className="px-4 py-3 text-end text-gray-600">{formatCurrency(row.original_price_nis)}</td>
                          <td className="px-4 py-3 text-end text-gray-600">
                            {row.discount_percent != null ? `${row.discount_percent}%` : '—'}
                          </td>
                          <td className="px-4 py-3 text-end font-medium text-gray-900">{formatCurrency(row.price_nis)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
